/**
 * Cross-tab persistence for MockProvider.
 *
 * Why this exists: a split-payment share link is opened in a *different tab*.
 * With the demo state living in in-memory Maps, that tab starts empty and the
 * share never resolves — the flow the whole feature exists to demonstrate is
 * the one flow that cannot be demonstrated.
 *
 * So mock state is persisted under one versioned key and re-read on every
 * access. Every tab reads the same snapshot, and a `storage` event lets the
 * table tab notice that a guest paid on their phone.
 *
 * What is stored: orders, splits, shares, payment attempts, reviews and the id
 * sequence — all of it public demo data that the backend would own for real.
 * What is NOT stored: audio, transcripts, credentials, or anything a backend
 * would call a secret. This is a demo fixture, not a database.
 */
import type { OrderOut, PaymentShareDetailOut, ReviewOut, SplitPaymentOut } from '@/types/api';

export const MOCK_STATE_KEY = 'garson:mock-state:v2';

/** Bump when the shape below changes; an older snapshot is discarded, not migrated. */
const SCHEMA_VERSION = 2;

export interface StoredOrder extends OrderOut {
  _sessionToken: string;
}

export interface StoredSplit extends SplitPaymentOut {
  _expired: boolean;
}

export interface StoredPayment {
  orderToken: string;
  amountIrr: number;
  status: string;
}

export interface MockState {
  version: number;
  sequence: number;
  /** Keyed by every alias an order answers to (public token, order token, id). */
  orders: Record<string, StoredOrder>;
  splits: Record<string, StoredSplit>;
  shares: Record<string, PaymentShareDetailOut>;
  payments: Record<string, StoredPayment>;
  reviews: ReviewOut[];
}

export function emptyMockState(): MockState {
  return {
    version: SCHEMA_VERSION,
    sequence: 1000,
    orders: {},
    splits: {},
    shares: {},
    payments: {},
    reviews: [],
  };
}

function storageAvailable(): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    const probe = `${MOCK_STATE_KEY}:probe`;
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

const canPersist = storageAvailable();

/**
 * In-memory mirror. It is the source of truth when localStorage is unavailable
 * (private mode, jsdom without storage) so the app degrades to single-tab
 * behaviour instead of breaking.
 */
let memory: MockState = emptyMockState();

function isMockState(value: unknown): value is MockState {
  if (!value || typeof value !== 'object') return false;
  const record = value as Partial<MockState>;
  return (
    record.version === SCHEMA_VERSION &&
    typeof record.sequence === 'number' &&
    typeof record.orders === 'object' &&
    typeof record.splits === 'object' &&
    typeof record.shares === 'object' &&
    typeof record.payments === 'object' &&
    Array.isArray(record.reviews)
  );
}

/** Reads the shared snapshot. Always hits storage, so another tab's write wins. */
export function readMockState(): MockState {
  if (!canPersist) return memory;
  try {
    const raw = window.localStorage.getItem(MOCK_STATE_KEY);
    if (raw === null) return memory;
    const parsed: unknown = JSON.parse(raw);
    if (!isMockState(parsed)) {
      // A snapshot from an older build: drop it rather than half-read it.
      window.localStorage.removeItem(MOCK_STATE_KEY);
      return memory;
    }
    memory = parsed;
    return parsed;
  } catch {
    return memory;
  }
}

export function writeMockState(state: MockState): void {
  memory = state;
  if (!canPersist) return;
  try {
    window.localStorage.setItem(MOCK_STATE_KEY, JSON.stringify(state));
  } catch {
    /* quota or private mode — in-memory state keeps this tab working */
  }
}

/**
 * Read-modify-write against the *current* shared snapshot.
 * Two tabs mutating at once still both land, because each mutation re-reads
 * immediately before writing rather than working from a stale closure.
 */
export function mutateMockState<T>(mutator: (state: MockState) => T): T {
  const state = readMockState();
  const result = mutator(state);
  writeMockState(state);
  return result;
}

/** Next deterministic id, persisted so two tabs never mint the same token. */
export function nextSequence(): number {
  return mutateMockState((state) => {
    state.sequence += 1;
    return state.sequence;
  });
}

/** Clears shared demo state. Used between tests and by the dev panel. */
export function resetMockState(): void {
  memory = emptyMockState();
  if (!canPersist) return;
  try {
    window.localStorage.removeItem(MOCK_STATE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Notifies this tab when another tab changes the demo state.
 * `storage` only fires in *other* tabs, which is exactly what the table tab
 * needs to hear when a guest pays their share on their own phone.
 */
export function subscribeToMockState(listener: (state: MockState) => void): () => void {
  if (!canPersist) return () => {};
  const handler = (event: StorageEvent) => {
    if (event.key !== null && event.key !== MOCK_STATE_KEY) return;
    listener(readMockState());
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
